import {
  JOURNEY_CONTEXT_LABEL,
  JOURNEY_PROJECTION_ERROR,
  JOURNEY_STEP_KIND,
} from "./journeyConstants";

const kindFor = (question) => {
  switch (question?.question_type) {
    case "patient_authentication":
      return JOURNEY_STEP_KIND.identity;
    case "matched_program_selection":
      return JOURNEY_STEP_KIND.matchedTreatments;
    case "treatment_screening_intro":
      return JOURNEY_STEP_KIND.screeningIntro;
    case "consent":
      return JOURNEY_STEP_KIND.consent;
    case "product_selection":
      return JOURNEY_STEP_KIND.products;
    case "labs":
    case "program_lab_checkout":
      return JOURNEY_STEP_KIND.labs;
    case "checkout":
      return JOURNEY_STEP_KIND.checkout;
    default:
      return question?.scope === "profile"
        ? JOURNEY_STEP_KIND.profile
        : JOURNEY_STEP_KIND.question;
  }
};

const withoutProgressSuffix = (value) =>
  typeof value === "string"
    ? value.replace(/\s*[·-]\s*\d+\s+of\s+\d+\s*$/i, "").trim()
    : "";

const contextFor = (question, companyName, kind) => {
  if (kind === JOURNEY_STEP_KIND.matchedTreatments ||
      kind === JOURNEY_STEP_KIND.products) {
    return JOURNEY_CONTEXT_LABEL.match;
  }
  if (kind === JOURNEY_STEP_KIND.checkout) {
    return JOURNEY_CONTEXT_LABEL.authorization;
  }
  if (kind === JOURNEY_STEP_KIND.consent) {
    return JOURNEY_CONTEXT_LABEL.consent;
  }
  if (kind === JOURNEY_STEP_KIND.screeningIntro) {
    return question?.screening_intro?.treatment_name ||
      question?.program_name ||
      "Clinical screening";
  }
  return withoutProgressSuffix(question?.section_progress_label) ||
    question?.program_name ||
    `${JOURNEY_CONTEXT_LABEL.welcomePrefix} ${companyName}`;
};

export class JourneyProjectionError extends Error {
  constructor(code, details) {
    super(details);
    this.name = "JourneyProjectionError";
    this.code = code;
  }
}

const stableId = (question, index) => {
  const value = question?.release_node_id || question?.id;
  if (!value) {
    throw new JourneyProjectionError(
      JOURNEY_PROJECTION_ERROR.missingItemId,
      `Visible journey item at index ${index} has no stable release ID.`,
    );
  }
  return String(value);
};

export const buildJourneyProjection = (questions, companyName = "") => {
  const source = Array.isArray(questions) ? questions : [];
  const steps = [];
  const seenItemIds = new Set();
  source.forEach((question, sourceIndex) => {
    const itemId = stableId(question, sourceIndex);
    if (seenItemIds.has(itemId)) {
      throw new JourneyProjectionError(
        JOURNEY_PROJECTION_ERROR.duplicateItemId,
        `Visible journey item '${itemId}' appears more than once.`,
      );
    }
    seenItemIds.add(itemId);
  });
  let index = 0;

  while (index < source.length) {
    const first = source[index];
    const kind = kindFor(first);
    let endIndex = index;
    if (kind === JOURNEY_STEP_KIND.products) {
      while (
        endIndex + 1 < source.length &&
        kindFor(source[endIndex + 1]) === JOURNEY_STEP_KIND.products
      ) {
        endIndex += 1;
      }
    }
    const groupedQuestions = source.slice(index, endIndex + 1);
    const itemIds = groupedQuestions.map((item, offset) =>
      stableId(item, index + offset)
    );
    const programIds = [
      ...new Set(groupedQuestions.map((item) => item?.program_id).filter(Boolean)),
    ].map(String);
    const treatmentTypeIds = [
      ...new Set(
        groupedQuestions.map((item) => item?.treatment_type_id).filter(Boolean),
      ),
    ].map(String);
    const treatmentTypeKeys = [
      ...new Set(
        groupedQuestions.map((item) => item?.treatment_type_key).filter(Boolean),
      ),
    ].map(String);
    steps.push({
      id: kind === JOURNEY_STEP_KIND.products
        ? `products:${itemIds.join("|")}`
        : itemIds[0],
      itemIds,
      kind,
      startIndex: index,
      endIndex,
      question: first,
      questions: groupedQuestions,
      itemKind: first?.question_type || first?.kind || "",
      scope: first?.scope || "common",
      programId: programIds.length === 1 ? programIds[0] : null,
      programIds,
      treatmentTypeId: treatmentTypeIds.length === 1
        ? treatmentTypeIds[0]
        : null,
      treatmentTypeIds,
      treatmentTypeKey: treatmentTypeKeys.length === 1
        ? treatmentTypeKeys[0]
        : null,
      treatmentTypeKeys,
      context: contextFor(first, companyName, kind),
      sectionName: first?.section_label || first?.section_name || "",
      sectionIndex: Number(first?.section_question_index || 0),
      sectionTotal: Number(first?.section_question_count || 0),
    });
    index = endIndex + 1;
  }

  return Object.freeze(steps.map((step, stepIndex) => Object.freeze({
    ...step,
    itemIds: Object.freeze([...step.itemIds]),
    questions: Object.freeze([...step.questions]),
    programIds: Object.freeze([...step.programIds]),
    treatmentTypeIds: Object.freeze([...step.treatmentTypeIds]),
    treatmentTypeKeys: Object.freeze([...step.treatmentTypeKeys]),
    position: stepIndex + 1,
    total: steps.length,
    progress: steps.length ? ((stepIndex + 1) / steps.length) * 100 : 0,
    backTargetId: stepIndex > 0 ? steps[stepIndex - 1].id : null,
    nextTargetId: stepIndex + 1 < steps.length
      ? steps[stepIndex + 1].id
      : null,
    previousIndex: stepIndex > 0 ? steps[stepIndex - 1].startIndex : null,
    nextIndex: stepIndex + 1 < steps.length
      ? steps[stepIndex + 1].startIndex
      : null,
  })));
};

export const activeJourneyStep = (steps, questionIndex) =>
  (Array.isArray(steps) ? steps : []).find(
    (step) => questionIndex >= step.startIndex && questionIndex <= step.endIndex,
  ) || null;

export const buildTerminalJourneyStep = (steps, definition) => {
  const projected = Array.isArray(steps) ? steps : [];
  const previous = projected[projected.length - 1] || null;
  const total = projected.length + 1;
  return Object.freeze({
    ...definition,
    itemIds: Object.freeze([]),
    questions: Object.freeze([]),
    programIds: Object.freeze([]),
    treatmentTypeIds: Object.freeze([]),
    treatmentTypeKeys: Object.freeze([]),
    question: null,
    position: total,
    total,
    progress: 100,
    backTargetId: previous?.id || null,
    nextTargetId: null,
    previousIndex: previous?.startIndex ?? null,
    nextIndex: null,
    startIndex: projected.length,
    endIndex: projected.length,
    sectionIndex: 1,
    sectionTotal: 1,
  });
};
